import { Session } from '@/utils/session/model';
import {
  AdminSessionDataArgs,
  getSessionData,
  clearSession as localStorageClearSession,
  SellerSessionDataArgs,
  setAdminSessionData,
  setSellerSessionData,
} from '@/utils/session/sessionManager';
import {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';

export interface GlobalContextDef {
  //Session
  // - undefined, when session is being retrieved
  // - null if no session was found
  // - Session, when a session is found
  session?: Session | undefined | null;
  setAdminSession?: (args: AdminSessionDataArgs) => void;
  setSellerSession?: (args: SellerSessionDataArgs) => void;
  clearSession: () => void;
  // SideBar Drawer
  sideMenuOpened: boolean;
  setSideMenuOpened?: Dispatch<SetStateAction<boolean>>;
  toggleSideBarMenu?: () => void;
}

const defaultValues: GlobalContextDef = {
  sideMenuOpened: false,
  clearSession: () => undefined,
};

const GlobalContext = createContext<GlobalContextDef>(defaultValues);

const useStatesAndActions = (): GlobalContextDef => {
  const [sideMenuOpened, setSideMenuOpened] = useState<boolean>(false);
  const [session, setSession] = useState<Session | null | undefined>();

  useEffect(() => {
    setSession(getSessionData());
  }, []);

  const clearSession = () => {
    localStorageClearSession();
    setSession(null);
  };

  const setSellerSession = (args: SellerSessionDataArgs) => {
    setSellerSessionData(args);
    setSession(getSessionData());
  };

  const setAdminSession = (args: AdminSessionDataArgs) => {
    setAdminSessionData(args);
    setSession(getSessionData());
  };

  return {
    session,
    setAdminSession,
    setSellerSession,
    clearSession,
    sideMenuOpened,
    setSideMenuOpened,
    toggleSideBarMenu: () => setSideMenuOpened(!sideMenuOpened),
  };
};

export const useGlobalContext = () => {
  const context = useContext(GlobalContext);
  if (!context) throw new Error('component is not inside GlobalContext');
  return context;
};

export const GlobalContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => (
  <GlobalContext.Provider value={useStatesAndActions()}>
    {children}
  </GlobalContext.Provider>
);
